import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import useMessage from "../../../hooks/useMessage";
import { useEffect, useState } from "react";
import { VALID_EMAIL_REGEX } from "../../../common/helpers/regexes";
import { isFulfilled } from "@reduxjs/toolkit";
import { updateTalentEmail } from "../settingsActions";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { Box, TextField } from "@mui/material";

type Props = {
  talentId: string;
  currentEmail: string;
  isOpen: boolean;
  onClose: () => void;
  onEmailUpdated: (newEmail: string) => void;
};

export const EditEmailDialog = ({
  talentId,
  currentEmail,
  isOpen,
  onClose,
  onEmailUpdated,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();

  const [emailValue, setEmailValue] = useState(currentEmail);
  const [emailError, setEmailError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form state when dialog opens with a new email
  useEffect(() => {
    setEmailValue(currentEmail);
    setEmailError(false);
  }, [currentEmail]);

  const handleEmailChange = (newEmail: string) => {
    setEmailValue(newEmail);
    if (!VALID_EMAIL_REGEX.test(newEmail)) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  };

  const onCancel = () => {
    setEmailValue(currentEmail);
    setEmailError(false);
    onClose();
  };

  const handleEmailUpdate = () => {
    // Skip update if email is invalid or unchanged
    if (emailError || emailValue === currentEmail) {
      onClose();
      return;
    }

    setIsSubmitting(true);
    dispatch(
      updateTalentEmail({
        talentId,
        emailAddress: emailValue,
      }),
    )
      .then((action) => {
        if (isFulfilled(action)) {
          sendMessage(t("common:updateSuccess"), "success");
          onEmailUpdated(emailValue);
          onClose();
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <DialogComponent
      open={isOpen}
      onClose={onClose}
      autoWidth
      sx={{ width: "400px" }}
    >
      <DialogContentWrapper
        title={t("settings:editEmail")}
        onSave={handleEmailUpdate}
        onCancel={onCancel}
        saveDisabled={emailError || isSubmitting}
        customSaveButtonLabel={t("common:save")}
      >
        <Box sx={{ width: "100%", py: 2, px: 2 }}>
          <TextField
            label={t("common:email")}
            value={emailValue}
            onChange={(e) => handleEmailChange(e.target.value)}
            fullWidth
            error={emailError}
            autoFocus
            disabled={isSubmitting}
            inputProps={{
              style: { paddingLeft: "8px" },
            }}
          />
        </Box>
      </DialogContentWrapper>
    </DialogComponent>
  );
};
